import { useEffect, useMemo, useRef } from "react";
import { animate } from "framer-motion";
import { geoNaturalEarth1 } from "d3-geo";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

const GEO_URL = "/world-50m.json";
const W = 980;
const H = 500;
const GOLD = "#C4A671";
const WORLD = { center: [-5, 18], zoom: 1.15 };
const CITY_ZOOM = 4.2;

export default function RegistryMap({ dishes, activeId, onSelect, interactive = true }) {
  const gRef = useRef(null);
  const markerRefs = useRef({});
  const cur = useRef(null);

  const projection = useMemo(
    () => geoNaturalEarth1().scale(175).center([0, 8]).translate([W / 2, H / 2]),
    []
  );

  useEffect(() => {
    const apply = ({ k, tx, ty }) => {
      cur.current = { k, tx, ty };
      if (gRef.current)
        gRef.current.setAttribute("transform", `translate(${tx} ${ty}) scale(${k})`);
      Object.values(markerRefs.current).forEach(
        (m) => m && m.setAttribute("transform", `scale(${1 / k})`)
      );
    };
    const dish = interactive ? dishes.find((d) => d.id === activeId) : null;
    let k, px, py;
    if (dish) {
      k = CITY_ZOOM;
      [px, py] = projection([dish.lon, dish.lat]);
    } else {
      k = WORLD.zoom;
      [px, py] = projection(WORLD.center);
    }
    const target = { k, tx: W / 2 - k * px, ty: H / 2 - k * py };
    if (!cur.current) {
      apply(target);
      return;
    }
    const from = { ...cur.current };
    const controls = animate(0, 1, {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (t) =>
        apply({
          k: from.k + (target.k - from.k) * t,
          tx: from.tx + (target.tx - from.tx) * t,
          ty: from.ty + (target.ty - from.ty) * t,
        }),
    });
    return () => controls.stop();
  }, [activeId, dishes, projection, interactive]);

  return (
    <div
      className="relative border"
      style={{ borderColor: "#1a1a1a", background: "#0D0D0D" }}
      data-testid="registry-map"
    >
      <ComposableMap
        projection="geoNaturalEarth1"
        projectionConfig={{ scale: 175, center: [0, 8] }}
        width={W}
        height={H}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          pointerEvents: interactive ? "auto" : "none",
        }}
      >
        <g ref={gRef}>
          <defs>
            <radialGradient id="reg-halo">
              <stop offset="0%" stopColor={GOLD} stopOpacity="0.35" />
              <stop offset="65%" stopColor={GOLD} stopOpacity="0.1" />
              <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
            </radialGradient>
          </defs>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#171717"
                  stroke="#2d2d2d"
                  strokeWidth={0.5}
                  vectorEffect="non-scaling-stroke"
                  style={{
                    default: { outline: "none" },
                    hover: { outline: "none", fill: "#1b1b1b" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
          {dishes.map((d) => (
            <Marker key={d.id} coordinates={[d.lon, d.lat]}>
              <g
                ref={(el) => {
                  markerRefs.current[d.id] = el;
                  if (el && cur.current)
                    el.setAttribute("transform", `scale(${1 / cur.current.k})`);
                }}
                style={{ cursor: interactive ? "pointer" : "default" }}
                onClick={() => onSelect && onSelect(d.id)}
                data-testid={`registry-marker-${d.city.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <circle r={activeId === d.id ? 14 : 9} fill="url(#reg-halo)" />
                <circle r={2.4} fill={activeId === d.id ? "#E8CF9E" : GOLD} />
                <circle r={2.4} fill="none" stroke={GOLD} strokeWidth={0.7} className="vdi-node-ring" />
              </g>
            </Marker>
          ))}
        </g>
      </ComposableMap>
    </div>
  );
}
